import { timelineData } from '../data/timeline';
import { formatNumber } from '../utils';
import { glossarySpan } from '../tooltip';

export function renderTimelineView(container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'view-container';
  container.appendChild(wrapper);

  const data = timelineData;
  const maxConflicts = Math.max(...data.map(d => d.stateBased + d.nonState + d.oneSided));
  const maxDeaths = Math.max(...data.map(d => d.totalDeaths));

  const W = 1200;
  const H = 400;
  const PAD = { top: 30, right: 80, bottom: 50, left: 60 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const barW = Math.max(6, (chartW / data.length) - 2);

  function x(i: number): number {
    return PAD.left + (i / (data.length - 1)) * chartW;
  }
  function yConflicts(v: number): number {
    return PAD.top + chartH - (v / maxConflicts) * chartH;
  }
  function yDeaths(v: number): number {
    return PAD.top + chartH - (v / maxDeaths) * chartH;
  }

  // Build death trend line
  const deathLine = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${yDeaths(d.totalDeaths)}`).join(' ');

  // Y-axis ticks for conflicts
  const conflictTicks = [0, 50, 100, 150, 200].filter(t => t <= maxConflicts + 10);
  const deathTicks = [0, 100000, 200000, 400000, 700000].filter(t => t <= maxDeaths + 50000);

  wrapper.innerHTML = `
    <div class="view-header">
      <h2>Conflict Timeline (1946–2024)</h2>
      <p>Number of active ${glossarySpan('State-based conflict', 'state-based')}, ${glossarySpan('Non-state conflict', 'non-state')}, and ${glossarySpan('One-sided violence', 'one-sided')} conflicts per year, with ${glossarySpan('Fatalities', 'fatality')} trend overlay.</p>
    </div>

    <div class="chart-panel">
      <h3>Active Conflicts by Type + Fatality Trend</h3>
      <p class="chart-subtitle">Stacked bars = conflict count (left axis) · Orange line = fatalities (right axis). Non-state and one-sided data available from 1989.</p>
      <svg class="chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">
        <!-- Grid lines -->
        ${conflictTicks.map(t => `
          <line x1="${PAD.left}" x2="${W - PAD.right}" y1="${yConflicts(t)}" y2="${yConflicts(t)}" stroke="#1e293b" stroke-dasharray="3,3"/>
          <text x="${PAD.left - 8}" y="${yConflicts(t) + 4}" text-anchor="end" class="bar-label" style="font-size:10px">${t}</text>
        `).join('')}

        <!-- Death axis ticks -->
        ${deathTicks.map(t => `
          <text x="${W - PAD.right + 8}" y="${yDeaths(t) + 4}" text-anchor="start" class="bar-label" style="font-size:10px;fill:#f59e0b">${t >= 1000 ? `${t / 1000}k` : t}</text>
        `).join('')}

        <!-- Bars -->
        ${data.map((d, i) => {
          const total = d.stateBased + d.nonState + d.oneSided;
          const h1 = (d.stateBased / maxConflicts) * chartH;
          const h2 = (d.nonState / maxConflicts) * chartH;
          const h3 = (d.oneSided / maxConflicts) * chartH;
          const bx = x(i) - barW / 2;
          const baseY = PAD.top + chartH;

          return `
            <g>
              <rect class="bar-rect" x="${bx}" y="${baseY - h1}" width="${barW}" height="${h1}" fill="#3b82f6" rx="1">
                <title>${d.year}: ${d.stateBased} state-based, ${d.nonState} non-state, ${d.oneSided} one-sided (${total} total) — ${formatNumber(d.totalDeaths)} deaths</title>
              </rect>
              <rect class="bar-rect" x="${bx}" y="${baseY - h1 - h2}" width="${barW}" height="${h2}" fill="#8b5cf6" rx="1">
                <title>${d.year}: ${d.nonState} non-state conflicts</title>
              </rect>
              <rect class="bar-rect" x="${bx}" y="${baseY - h1 - h2 - h3}" width="${barW}" height="${h3}" fill="#ec4899" rx="1">
                <title>${d.year}: ${d.oneSided} one-sided violence</title>
              </rect>
            </g>
          `;
        }).join('')}

        <!-- Death trend line -->
        <path d="${deathLine}" fill="none" stroke="#f59e0b" stroke-width="2" stroke-opacity="0.7"/>
        ${data.map((d, i) => `
          <circle cx="${x(i)}" cy="${yDeaths(d.totalDeaths)}" r="3" fill="#f59e0b" opacity="0.8">
            <title>${d.year}: ~${formatNumber(d.totalDeaths)} fatalities</title>
          </circle>
        `).join('')}

        <!-- X-axis labels (every ~5 items) -->
        ${data.filter((_, i) => i % 3 === 0 || i === data.length - 1).map((d) => {
          const idx = data.indexOf(d);
          return `<text x="${x(idx)}" y="${H - 10}" text-anchor="middle" class="bar-label" style="font-size:10px">${d.year}</text>`;
        }).join('')}

        <!-- Axis labels -->
        <text x="${PAD.left - 40}" y="${PAD.top + chartH / 2}" text-anchor="middle" transform="rotate(-90, ${PAD.left - 40}, ${PAD.top + chartH / 2})" class="bar-label" style="font-size:11px">Active Conflicts</text>
        <text x="${W - PAD.right + 50}" y="${PAD.top + chartH / 2}" text-anchor="middle" transform="rotate(90, ${W - PAD.right + 50}, ${PAD.top + chartH / 2})" class="bar-label" style="font-size:11px;fill:#f59e0b">Fatalities</text>
      </svg>

      <div style="display:flex;gap:20px;margin-top:12px;font-size:var(--font-size-sm);color:var(--text-secondary);">
        <span><span style="color:#3b82f6">■</span> State-based</span>
        <span><span style="color:#8b5cf6">■</span> Non-state</span>
        <span><span style="color:#ec4899">■</span> One-sided</span>
        <span><span style="color:#f59e0b">—</span> Fatalities</span>
      </div>
    </div>

    <div class="chart-grid" style="margin-top: var(--space-2xl);">
      <div class="chart-panel">
        <h3>Key Observations</h3>
        <p class="chart-subtitle">What the data reveals about global conflict trends</p>
        <ul style="list-style:none;padding:0;">
          <li style="padding:8px 0;border-bottom:1px solid var(--border-subtle);color:var(--text-secondary);font-size:var(--font-size-sm);">
            <strong style="color:var(--status-bad)">2024: Historic peak</strong> — 61 state-based conflicts, the highest since UCDP began tracking in 1946.
          </li>
          <li style="padding:8px 0;border-bottom:1px solid var(--border-subtle);color:var(--text-secondary);font-size:var(--font-size-sm);">
            <strong style="color:var(--status-warn)">1994 spike</strong> — The Rwandan genocide caused a massive fatality spike (~700,000) despite moderate conflict counts.
          </li>
          <li style="padding:8px 0;border-bottom:1px solid var(--border-subtle);color:var(--text-secondary);font-size:var(--font-size-sm);">
            <strong style="color:var(--status-info)">Non-state rise</strong> — Non-state conflicts (gang violence, militia clashes) have surged since 2011, now exceeding state-based counts.
          </li>
          <li style="padding:8px 0;color:var(--text-secondary);font-size:var(--font-size-sm);">
            <strong style="color:var(--status-info)">2022 surge</strong> — Russia's invasion of Ukraine pushed fatalities to 205,000, the deadliest year since 1994.
          </li>
        </ul>
      </div>
    </div>
  `;
}
